// frontend/src/hooks/useUserMutations.ts — the users write surface (associate ·
// create · remove · disable · reactivate). Each invalidates the `['users',
// projectId]` PREFIX on success, so the list + any open detail/search refetch
// together (FRONTEND-PATTERNS §Mutations + invalidation).
//
// Errors are NOT caught here — they bubble so the form shell / ConfirmDialog map
// the backend `code` through errors.json. 401 is the apiClient's job (ADR-003);
// these never touch it. (No invalidate(['me']) — these mutate OTHER users, not
// the acting user's own state — §Self-mutating mutations.)

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import type { AssociateUserRequest, CreateUserRequest, EditUserInProjectRequest, HandoverRequest } from '@ems-app/schemas/users/requests';
import type { CreatedUserResponse, GrantWebAccessResponse, RemoveUserResponse } from '@ems-app/schemas/users/responses';
import { apiClient } from '../lib/apiClient.js';
import { clustersKeys } from './useClusters.js';
import { sitesKeys } from './useSites.js';
import { usersKeys } from './useUsers.js';
import { chainsKeys } from './useChains.js';
import { zonesKeys } from './useZones.js';
import { refreshOnSettled } from '../lib/settled.js';

function useInvalidateUsers(projectId: string): () => void {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: usersKeys.all(projectId) });
}

/** The chains a person held moved or ended (the handover · a leave with its gaps confirmed): every level's chain
 *  reads, by their prefixes — the places' Responsibilities sections and their editors re-read. */
function useInvalidateChains(projectId: string): () => void {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: chainsKeys.all(projectId) });
    void qc.invalidateQueries({ queryKey: zonesKeys.all(projectId) });
    void qc.invalidateQueries({ queryKey: clustersKeys.all(projectId) });
    void qc.invalidateQueries({ queryKey: sitesKeys.all(projectId) });
  };
}

/** POST …/users/associate — link an existing pool user with a membership. */
export function useAssociateUser(
  projectId: string,
): UseMutationResult<CreatedUserResponse, Error, AssociateUserRequest> {
  const invalidate = useInvalidateUsers(projectId);
  return useMutation({
    mutationFn: async (body) => (await apiClient.post<CreatedUserResponse>(`/projects/${projectId}/users/associate`, body)).data,
    onSuccess: invalidate,
  });
}

/** POST …/users — create a new global user AND add to this project. */
export function useCreateUser(
  projectId: string,
): UseMutationResult<CreatedUserResponse, Error, CreateUserRequest> {
  const invalidate = useInvalidateUsers(projectId);
  return useMutation({
    mutationFn: async (body) => (await apiClient.post<CreatedUserResponse>(`/projects/${projectId}/users`, body)).data,
    onSuccess: invalidate,
  });
}

/** PATCH …/users/:id — edit a user's MEMBERSHIP in this project (role / scope /
 *  admin flag; identity is never edited from a project URL — backend contract).
 *  204 No Content; invalidate refreshes list + detail + peek together. */
export function useEditUser(
  projectId: string,
): UseMutationResult<void, Error, { userId: string; body: EditUserInProjectRequest }> {
  const invalidate = useInvalidateUsers(projectId);
  return useMutation({
    mutationFn: async ({ userId, body }) => {
      await apiClient.patch(`/projects/${projectId}/users/${userId}`, body);
    },
    onSuccess: invalidate,
  });
}

/** DELETE …/users/:id — unlink from this project; returns remaining count.
 *  `disable: true` is the sole-membership "remove & disable" flow — one atomic
 *  call (the backend deactivates the orphaned account in the same transaction),
 *  replacing the old non-atomic disable-then-remove sequence. */
export function useRemoveUser(
  projectId: string,
): UseMutationResult<RemoveUserResponse, Error, { userId: string; disable?: boolean; confirmGaps?: boolean }> {
  const qc = useQueryClient();
  const invalidateChains = useInvalidateChains(projectId);
  return useMutation({
    // `confirmGaps`: the dialog showed the chains the person still holds and the operator leaves the gaps the
    // handover did not fill (ADR-081 §4) — without it a held chain answers 409 CHAINS_END.
    mutationFn: async ({ userId, disable, confirmGaps }) =>
      (
        await apiClient.delete<RemoveUserResponse>(`/projects/${projectId}/users/${userId}`, {
          params: { ...(disable ? { disable: 'true' } : {}), ...(confirmGaps ? { confirm_gaps: 'true' } : {}) },
        })
      ).data,
    // Refresh the list + the search pool (in_project flags) — but NOT the removed
    // user's detail. Invalidating the detail would refetch it and 404 (they're no
    // longer a member of this project), tripping the apiClient's not-found redirect
    // before the detail page can navigate back to the list. Leaving it untouched, it
    // simply goes inactive when the page unmounts on the post-remove navigation. A REFUSED remove leaves the person
    // in: their detail re-reads too.
    // The gaps confirmed, the person's chains ended with the row — the places' chains re-read.
    onSettled: refreshOnSettled((_d, { userId, confirmGaps }, err) => {
      void qc.invalidateQueries({ queryKey: usersKeys.all(projectId), exact: true });
      void qc.invalidateQueries({ queryKey: [...usersKeys.all(projectId), 'search'] });
      if (err) void qc.invalidateQueries({ queryKey: usersKeys.detail(projectId, userId) });
      if (confirmGaps) invalidateChains();
    }),
  });
}

/** POST …/users/:id/disable — global deactivate + force-logout. May throw
 *  USER_IN_MULTIPLE_PROJECTS (non-sysadmin, multi-project target). */
export function useDisableUser(projectId: string): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidateUsers(projectId);
  return useMutation({
    mutationFn: async (userId) => { await apiClient.post(`/projects/${projectId}/users/${userId}/disable`); },
    onSettled: refreshOnSettled(invalidate),
  });
}

/** POST …/users/:id/reactivate — clear the global disable flag. */
export function useReactivateUser(projectId: string): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidateUsers(projectId);
  return useMutation({
    mutationFn: async (userId) => { await apiClient.post(`/projects/${projectId}/users/${userId}/reactivate`); },
    onSettled: refreshOnSettled(invalidate),
  });
}

/** POST …/users/:id/grant-web-access — admin-triggered setup link: an INVITATION
 *  for a target with no password (its first credential), a RESET link for one
 *  that has it; the user sets their own secret either way. Returns the SHOW-ONCE
 *  payload (the raw link + expiry + which couriers ran) — the caller presents it
 *  once; it can never be re-fetched (only its hash is stored). Invalidates the
 *  users prefix: chip 1 flips to Invited on the list/detail. May throw
 *  NO_LOCAL_PASSWORD (SSO) / USER_NOT_ACTIVE / INVITE_JUST_SENT / USER_NOT_FOUND. */
export function useGrantWebAccess(
  projectId: string,
): UseMutationResult<GrantWebAccessResponse, Error, string> {
  const invalidate = useInvalidateUsers(projectId);
  return useMutation({
    mutationFn: async (userId) =>
      (await apiClient.post<GrantWebAccessResponse>(`/projects/${projectId}/users/${userId}/grant-web-access`)).data,
    onSettled: refreshOnSettled(invalidate),
  });
}

/** DELETE …/users/:id/web-invite — cancel the outstanding web invitation (the
 *  grant's symmetry): the sent link stops working; the invited/invite-expired
 *  chip clears back to none. 404 INVITE_NOT_FOUND when nothing is outstanding. */
export function useRevokeWebInvite(projectId: string): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidateUsers(projectId);
  return useMutation({
    mutationFn: async (userId) => {
      await apiClient.delete(`/projects/${projectId}/users/${userId}/web-invite`);
    },
    onSettled: refreshOnSettled(invalidate),
  });
}

/** POST …/users/:id/handover — the person's chains given to successors, or left (ADR-081 §5): per place a
 *  successor or none, every place through the chain's one choke point in one transaction. May throw
 *  ASSIGNEE_NOT_BACKED (the backing consent — `grant_backing`), ASSIGNEE_NOT_GRANTABLE, HANDOVER_NOT_HELD. */
export function useHandover(projectId: string): UseMutationResult<void, Error, { userId: string; body: HandoverRequest }> {
  const invalidateUsers = useInvalidateUsers(projectId);
  const invalidateChains = useInvalidateChains(projectId);
  return useMutation({
    mutationFn: async ({ userId, body }) => {
      await apiClient.post(`/projects/${projectId}/users/${userId}/handover`, body);
    },
    // The person's chains moved: the people (their detail's Responsibilities card, the impact read) and every level's chains.
    onSettled: refreshOnSettled(() => {
      invalidateUsers();
      invalidateChains();
    }),
  });
}
